import React, { useEffect } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

const HizmetlerPage: React.FC = () => {
  useEffect(() => {
    // SEO Meta Tags
    document.title = 'İSG Hizmetleri - Selçuklu Konya İş Sağlığı Güvenliği Özgür OSGB';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Konya Selçuklu İş Sağlığı ve Güvenliği hizmetleri: İş güvenliği uzmanı, işyeri hekimi, risk analizi, İSG eğitimleri, periyodik kontroller ve acil durum planları. Özgür OSGB ile güvenli çalışma ortamı.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'İSG Hizmetleri, İş Güvenliği Uzmanı, İşyeri Hekimi, Risk Analizi, İSG Eğitimi');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://ozgurisg.net'}/hizmetler`);
    }

    // Schema.org JSON-LD
    const existingScript = document.querySelector('script[type="application/ld+json"][data-page="services"]');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-page', 'services');
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "İş Sağlığı ve Güvenliği Hizmetleri",
      "description": "Kapsamlı İSG hizmetleri: İş güvenliği uzmanı, işyeri hekimi, risk analizi, eğitimler ve periyodik kontroller",
      "provider": {
        "@type": "LocalBusiness",
        "name": "Özgür OSGB",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Sancak Mahallesi",
          "addressLocality": "Selçuklu",
          "addressRegion": "Konya",
          "addressCountry": "TR"
        }
      },
      "serviceType": "İş Sağlığı ve Güvenliği",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "İSG Hizmet Kataloğu",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "İş Güvenliği Uzmanı",
              "description": "Risk değerlendirmesi, güvenlik planlaması ve periyodik kontroller"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "İşyeri Hekimi",
              "description": "Sağlık muayeneleri, meslek hastalığı takibi ve sağlık raporları"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Risk Analizi",
              "description": "Detaylı risk analizi, önleyici tedbirler ve raporlama"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "İSG Eğitimleri",
              "description": "Temel İSG eğitimi, özel eğitimler ve sertifikalandırma"
            }
          }
        ]
      }
    });
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.querySelector('script[type="application/ld+json"][data-page="services"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-600 to-teal-600 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Hizmetlerimiz</h1>
          <p className="text-xl max-w-3xl mx-auto">
            İş Sağlığı ve Güvenliği alanında kapsamlı hizmetler sunarak işyerlerinizde güvenli çalışma ortamı oluşturuyoruz.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            {/* İş Güvenliği Uzmanı */}
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">İş Güvenliği Uzmanı</h3>
              <p className="text-gray-600 mb-6">
                Uzman kadromuzla işyerinizdeki riskleri analiz eder, güvenlik önlemlerini belirler ve uygulanmasını sağlarız.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  Risk değerlendirmesi
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  Güvenlik planlaması
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  Periyodik kontroller
                </li>
              </ul>
            </div>

            {/* İşyeri Hekimi */}
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-stethoscope-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">İşyeri Hekimi</h3>
              <p className="text-gray-600 mb-6">
                Çalışanlarınızın sağlık durumunu takip eder, meslek hastalıklarını önleyici tedbirler alırız.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  Sağlık muayeneleri
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  Meslek hastalığı takibi
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  Sağlık raporları
                </li>
              </ul>
            </div>

            {/* Risk Analizi */}
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-search-line text-3xl text-red-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Risk Analizi</h3>
              <p className="text-gray-600 mb-6">
                İşyerinizdeki tüm riskleri detaylı olarak analiz eder, önleyici tedbirleri belirleriz.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  Detaylı risk analizi
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  Önleyici tedbirler
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  Raporlama
                </li>
              </ul>
            </div>

            {/* İSG Eğitimleri */}
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-graduation-cap-line text-3xl text-purple-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">İSG Eğitimleri</h3>
              <p className="text-gray-600 mb-6">
                Çalışanlarınıza İş Sağlığı ve Güvenliği konularında kapsamlı eğitimler veriyoruz.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  Temel İSG eğitimi
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  Özel eğitimler
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  Sertifikalandırma
                </li>
              </ul>
            </div>

            {/* Periyodik Kontroller */}
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-calendar-check-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Periyodik Kontroller</h3>
              <p className="text-gray-600 mb-6">
                İşyerinizdeki güvenlik önlemlerinin düzenli kontrollerini yaparak sürekli güvenliği sağlarız.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  Düzenli denetimler
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  Güvenlik kontrolleri
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  Raporlama
                </li>
              </ul>
            </div>

            {/* Acil Durum Planları */}
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-alarm-warning-line text-3xl text-yellow-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Acil Durum Planları</h3>
              <p className="text-gray-600 mb-6">
                İşyeriniz için acil durum planları hazırlayarak olası risklere karşı hazırlıklı olmanızı sağlarız.
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  Acil durum planları
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  Tahliye planları
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-green-600 mr-2"></i>
                  Eğitim ve tatbikatlar
                </li>
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gray-50 py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            İşyeriniz İçin En Uygun Çözümü Bulalım
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Uzman kadromuzla işyerinizdeki ihtiyaçları analiz ederek size özel çözümler sunuyoruz.
          </p>
          <a 
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.open('https://wa.me/903323028232?text=Merhaba, İSG hizmetleriniz hakkında bilgi almak istiyorum.', '_blank');
            }}
            className="inline-flex items-center justify-center font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap bg-green-600 text-white hover:bg-green-700 px-8 py-4 text-lg"
          >
            Ücretsiz Teklif Alın
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HizmetlerPage;